/**
 * Create an invitation link for a workspace (or, with projectIds, for a project) and
 * show it. Shared by the Collaborators page and the project collaborators dialog.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../hooks/useProjects";
import { useCreateInvite, useMyWorkspaceRole } from "../../hooks/useWorkspaceMembers";
import { normalizePhoneNumber } from "../../lib/phone";
import { callableErrorCode, inviteProjectIds, uploaderNeedsProject } from "../../lib/invite";
import type { InviteRole } from "../../services/naraDoor";
import { InviteLinkResult } from "./InviteLinkResult";

const ROLES: InviteRole[] = ["uploader", "viewer", "editor", "admin"];

interface InviteFormProps {
  workspaceId: string;
  /** Set for a project invite: every role is scoped to this project and Field Agent is the default. */
  projectId?: string;
  onCancel: () => void;
  onDone: () => void;
}

const inputClass = "w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

/** Checkbox list of the active workspace's projects, for a Field Agent invited from the Members page. */
export function ProjectPicker({ selected, onToggle }: { selected: string[]; onToggle: (id: string) => void }) {
  const { t } = useTranslation();
  const projects = useProjects().data?.projects ?? [];
  return (
    <div>
      <span className="block text-sm font-medium text-foreground mb-1">{t("invite.form.projects")}</span>
      {projects.length === 0 && <p className="text-sm text-muted-foreground">{t("invite.form.noProjects")}</p>}
      {projects.map((p) => (
        <label key={p.id} className="flex items-center gap-2 py-1">
          <input type="checkbox" checked={selected.includes(p.id)} onChange={() => onToggle(p.id)} className="h-4 w-4 rounded border-border" />
          <span className="text-sm text-foreground">{p.name}</span>
        </label>
      ))}
    </div>
  );
}

export function InviteForm({ workspaceId, projectId, onCancel, onDone }: InviteFormProps) {
  const { t } = useTranslation();
  const createInvite = useCreateInvite();
  // Only the owner gives the admin role (the server refuses it from an admin).
  const roles = useMyWorkspaceRole() === "owner" ? ROLES : ROLES.filter((r) => r !== "admin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<InviteRole>(projectId ? "uploader" : "editor");
  const [pickedProjects, setPickedProjects] = useState<string[]>([]);
  const [send, setSend] = useState(true);
  const [error, setError] = useState<string | null>(null);

  if (createInvite.data) {
    return <InviteLinkResult invite={createInvite.data} onDone={onDone} />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t("invite.errors.nameRequired"));
      return;
    }
    const projectIds = inviteProjectIds(role, projectId, pickedProjects);
    if (uploaderNeedsProject(role, projectIds)) {
      setError(t("invite.errors.projectRequired"));
      return;
    }
    createInvite.mutate(
      {
        workspaceId,
        role,
        ...(projectIds && { projectIds }),
        inviteeName: name.trim(),
        inviteeEmail: email.trim() || undefined,
        inviteePhone: phone.trim() ? normalizePhoneNumber(phone.trim()) : undefined,
        send,
      },
      {
        onError: (err) => {
          console.error("createInvite failed", callableErrorCode(err));
          setError(t("invite.errors.createFailed"));
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">{t("invite.form.name")}</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("invite.form.namePlaceholder")} className={inputClass} autoFocus />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">{t("invite.form.email")}</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("invite.form.emailPlaceholder")} className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">{t("invite.form.phone")}</label>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("invite.form.phonePlaceholder")} className={inputClass} />
        <p className="text-xs text-muted-foreground mt-1">{t("invite.form.contactHint")}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">{t("invite.form.role")}</label>
        <select value={role} onChange={(e) => setRole(e.target.value as InviteRole)} className={inputClass}>
          {roles.map((r) => (
            <option key={r} value={r}>{t(`workspaceMembers.roles.${r}`)}</option>
          ))}
        </select>
      </div>

      {role === "uploader" && !projectId && (
        <ProjectPicker
          selected={pickedProjects}
          onToggle={(id) => setPickedProjects((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))}
        />
      )}

      <label className="flex items-center gap-2">
        <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} className="h-4 w-4 rounded border-border" />
        <span className="text-sm text-foreground">{t("invite.form.send")}</span>
      </label>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors">
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          disabled={createInvite.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createInvite.isPending ? t("invite.form.creating") : t("invite.form.submit")}
        </button>
      </div>
    </form>
  );
}
